import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ArtifactsGuide } from './ArtifactsGuide'
import { useArtifactsWhere } from './contentHost'
import { SharedWithMe } from './SharedWithMe'
import { SidebarSection } from '../sidebar/SidebarSection'
import { useSections } from '../sidebar/useSections'
import type { SharedArtifact } from '../transport/relay'
import { groupByMachine } from '../shell/machineGroups'
import { StagePage, type StageChrome } from '../shell/StagePage'
import {
  artifactState,
  artifactsSettled,
  getArtifactsEverywhere,
  mergeArtifacts,
  type ArtifactRow,
  type ArtifactsEverywhere,
} from './everywhere'

/**
 * THE ACCOUNT'S ARTIFACTS, asked of every machine — ONE query, read by the rail's
 * Artifacts button (its count) and by this page (its rows), and subscribed to by the
 * viewer, so the list is fetched once and the permission dial can never disagree with the
 * row behind it.
 *
 * Fresh for a few seconds, not none: the rail's button asks when More opens, and this page
 * mounting a moment later would otherwise ask every machine a second time.
 */
export function useArtifactsList() {
  const qc = useQueryClient()
  return useQuery({
    queryKey: ['artifacts'],
    queryFn: () =>
      getArtifactsEverywhere({
        /*
         * How many machines will answer, written into the cache BEFORE any of
         * them do — otherwise the first empty answer makes the list look
         * complete. See the empty state below.
         */
        onExpected: (n) => {
          qc.setQueryData<ArtifactsEverywhere>(['artifacts'], (prev) =>
            prev
              ? { ...prev, expected: Math.max(prev.expected, n) }
              : { rows: [], machines: [], listError: null, expected: n },
          )
        },
        onMachine: (m) => {
          qc.setQueryData<ArtifactsEverywhere>(['artifacts'], (prev) => mergeArtifacts(prev, m))
        },
      }),
    staleTime: 15_000,
  })
}

/**
 * MORE › ARTIFACTS, ON THE STAGE — owner, 2026-10-22: the rail holds one button, and the
 * list is a page of the stage; a row opens the artifact there (the viewer, over this page),
 * and its Back comes back here.
 */
export function ArtifactsPage({
  onOpenArtifact,
  onOpenShared,
  ...chrome
}: StageChrome & {
  onOpenArtifact: (a: ArtifactRow) => void
  /** The viewer's door for an artifact a FRIEND shares — a different subject from one of
   *  this account's own, with none of the owner's controls (owner, 2026-10-05). */
  onOpenShared: (a: SharedArtifact) => void
}) {
  /*
   * WHERE ARTIFACT PAGES ARE SERVED, asked HERE — owner, 2026-10-05: artifacts move to a
   * hostname of their own. Both lists below open rows whose frame depends on the answer,
   * and a row can only be tapped while this page is up, so the page is what asks: by the
   * time a row is on screen the answer is normally in the cache, where the row and the
   * viewer read it (artifacts/contentHost.ts). Nothing here renders from it.
   */
  useArtifactsWhere()
  const artifacts = useArtifactsList()
  const rows = artifacts.data?.rows ?? []
  const machines = artifacts.data?.machines ?? []
  /*
   * A machine that could not be asked is NAMED, not dropped. A machine that is
   * merely off must never make its artifacts look deleted — the same rule the
   * agent roster learned, for the same reason.
   */
  const quiet = machines.filter((m) => m.error)
  /*
   * ⚠️ ISPENDING IS NOT "STILL ASKING". The fan-out writes each machine's answer
   * into this same cache entry as it arrives, so react-query stops being pending
   * the moment the FIRST one lands — with five more still out. Measured on dev:
   * the panel said "Nothing published yet" for about ten seconds over an account
   * that publishes one artifact, because the first machine to answer had none.
   *
   * An empty state is a CLAIM about every machine. It may only be made once
   * every machine has actually answered.
   */
  const settled = artifactsSettled(artifacts.data)
  const asked = machines.length
  const total = artifacts.data?.expected ?? 0
  /*
   * ONE GROUP PER MACHINE, opening and closing like the Agents rail's — owner,
   * 2026-09-29: 把不同機器上面的 artifact 分類起來, 讓我們可以統一看到單一機器上面所持有
   * 的 artifact. The machine's name is the heading (the name the account shows for
   * it), so the row below no longer repeats it. Open/closed is remembered with the
   * rail's own sections.
   */
  const { isOpen, toggle } = useSections()
  const nameOf = (id: string, fallback: string) => machines.find((m) => m.device_id === id)?.name || fallback
  const groups = groupByMachine(rows, (a) => a.device_id, (a) => nameOf(a.device_id, a.machine))

  return (
    <StagePage
      {...chrome}
      title="Artifacts"
      sub={rows.length > 0 ? `${rows.length} published` : undefined}
      backLabel="Back"
      className="more-page"
    >
      {!settled && (
        <p className="more-note">
          Asking your machines…
          {total > 0 && (
            <>
              {' '}
              {asked} of {total} answered
            </>
          )}
        </p>
      )}

      {/*
        NOT one line of CLI. Owner, 2026-09-20: 「讓使用者可以知道他可以讓他的
        Agent 使用 Avengents Artifact 這一項功能,這樣子他的使用者就知道那個欄位
        存在的意義是什麼」 — the only person who ever reads this has never
        published an artifact, so it has to say what one IS before it says
        how. ArtifactsGuide carries the words; the distinction it needs from
        here is whether there is any machine to publish FROM.
      */}
      {settled && rows.length === 0 && <ArtifactsGuide hasMachines={machines.length > 0} />}

      {groups.map((g) => {
        const m = machines.find((x) => x.device_id === g.deviceId)
        const id = `more-art:${g.deviceId}`
        return (
          <SidebarSection key={g.deviceId} id={id} label={g.name} count={g.rows.length} isOpen={isOpen(id)} onToggle={toggle}>
            {g.rows.map((a) => {
              const st = artifactState(a, m?.link ?? null, m?.reachable ?? 'unknown')
              return (
                <button
                  key={`${a.device_id}:${a.hash}`}
                  type="button"
                  className="sb-row art-row"
                  onClick={() => onOpenArtifact(a)}
                  title={`${a.source} on ${a.machine} — ${st.why}`}
                >
                  <span className={`art-dot art-${st.tone}`} aria-hidden="true" />
                  <span className="art-row-main">
                    <span className="art-row-name">{a.summary || a.name || a.hash.slice(0, 8)}</span>
                    <span className="art-row-sub">
                      {st.word}
                      {' · '}
                      {a.visibility}
                    </span>
                  </span>
                </button>
              )
            })}
          </SidebarSection>
        )
      })}

      {quiet.length > 0 && (
        <p className="more-note more-quiet">
          Could not ask {quiet.map((m) => m.name).join(', ')} — anything published there is not listed above.
        </p>
      )}

      {/* Owner, 2026-10-05: one more group — what OTHER people share with this
          account. Its own query (the cloud answers it, no machine is asked), and its
          own door into the viewer: a friend's artifact is never handed to
          onOpenArtifact. SharedWithMe says where each row opens, and why. */}
      <SharedWithMe isOpen={isOpen} onToggle={toggle} onOpenShared={onOpenShared} />

      {artifacts.data?.listError && (
        <p className="more-note more-quiet">Could not read this account&rsquo;s machines: {artifacts.data.listError}</p>
      )}
    </StagePage>
  )
}
